/**
 * Network policy for the Mangrove POS tRPC client (P2-21).
 *
 * The link chain is shared by every consumer (admin / terminal / kitchen / ops),
 * so the wire format, batching, credentials, and dev-time logging are decided
 * here — not in each app.
 *
 *   1. `loggerLink`       → console output for failed downlinks + (in dev) every
 *                            request. Disabled in prod success paths so the
 *                            console stays useful for app code.
 *   2. `httpBatchLink`    → coalesces concurrent calls onto one HTTP request,
 *                            applies the SuperJSON transformer (so Date / bigint
 *                            from `@mangrove/db` ADR-0029 round-trip correctly), and
 *                            sends the Better-Auth session cookie via
 *                            `credentials: 'include'`.
 *      A call marked `SKIP_BATCH` (a model writing its answer) goes on its own
 *      request instead (`httpLink`, same transformer and cookie): see below.
 *      One marked `KEEPALIVE` does too, and outlives the page (#1030).
 *
 * Returns `TRPCLink<AnyTRPCRouter>[]` — the link chain is router-agnostic at
 * runtime, so the looser type lets the same array feed any `AppRouter`. The
 * higher-level `createPosTrpcClient<TRouter>` (vanilla) and
 * `createPosReactTrpcClient<TRouter>` (React) helpers do the assertion in
 * one place so consumers don't see a cast at their call site.
 */
import {
  httpBatchLink,
  httpLink,
  loggerLink,
  splitLink,
  type HTTPHeaders,
  type TRPCLink,
} from '@trpc/client';
import { markRequestStart, setAwakeProbe } from './slow-request.js';
import type { AnyTRPCRouter } from '@trpc/server';
import superjson from 'superjson';

export interface CreatePosTrpcLinksOptions {
  /**
   * Absolute or relative URL of the tRPC endpoint exposed by `@mangrove/api`.
   * Same-origin apps typically pass `'/trpc'`; cross-origin (eg. local dev
   * with the API on :4000 and the SPA on :5173) pass the full URL.
   */
  url: string;
  /**
   * Optional headers attached to every request. The function form is called
   * once per outgoing batch — read tenant id, locale, or device id from the
   * latest store state without re-creating the link.
   */
  headers?: HTTPHeaders | (() => HTTPHeaders | Promise<HTTPHeaders>);
  /**
   * Override the global `fetch`. Tests inject a mock; production apps leave
   * this alone so cookies are sent automatically.
   */
  fetch?: typeof fetch;
  /**
   * Override `loggerLink` enablement. Defaults to: log everything in dev,
   * log only failed downlinks in prod. Pass `() => false` to silence.
   */
  loggerEnabled?: (op: { direction: 'up' | 'down' }) => boolean;
  /**
   * #803: called when the session's workspace changed under this tab without
   * this tab switching it. Defaults to dispatching {@link WORKSPACE_CHANGED_EVENT}
   * on `window`, which `PosTrpcProvider` answers.
   */
  onWorkspaceChanged?: (change: WorkspaceChange) => void;
  /**
   * #803: whether this app is bound to the session's workspace (default true:
   * the tenant admin, the learner app, the tills). Mangrove HQ passes false: it
   * answers for Mangrove whatever workspace the session is in, so it neither
   * sends a belief nor catches up when a cofounder opens a client's workspace
   * in another tab.
   */
  workspaceBound?: boolean;
}

/**
 * #803 — the API's `x-mangrove-workspace` (apps/api/src/trpc/workspace-header.ts).
 * On a response: the workspace the session is in. On a request: the workspace
 * this tab believes it is in, so a WRITE from a tab still showing another one
 * is refused before it runs.
 */
export const WORKSPACE_HEADER = 'x-mangrove-workspace';

/** #950: the header that names this tab to the API (see {@link TAB_CLIENT_ID}). */
export const CLIENT_ID_HEADER = 'x-client-id';

function newTabId(): string {
  // randomUUID needs a secure context; a LAN-tethered tablet on http has none.
  const c = (globalThis as { crypto?: Crypto }).crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  return `t${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
}

/**
 * #950: a random id for this tab (this page load), sent on every call as
 * `x-client-id`. The API hands it back on the live change a write causes, so
 * the writer's tab does not reload what its own write just reloaded. It names
 * nothing and nobody; a new page load gets a new one.
 */
export const TAB_CLIENT_ID: string = newTabId();

/** Dispatched on `window` when the workspace changed under this tab. */
export const WORKSPACE_CHANGED_EVENT = 'mangrove:workspace-changed';

/**
 * #823: dispatched on `window` (detail: `{ tenantId }`) when a page restores
 * its saved copy, with the workspace that copy shows, so the page's first
 * call already says which workspace it is for (`PosTrpcProvider` sends it).
 */
export const WORKSPACE_SEED_EVENT = 'mangrove:workspace-seed';

/**
 * #819 — the API's `x-mangrove-surface` (apps/api/src/trpc/workspace-header.ts).
 * The HQ app sends {@link HQ_SURFACE} on every call (and `?surface=hq` on its
 * event stream), and for Mangrove staff the API runs it in Mangrove's own
 * workspace, whatever workspace the session is in ("HQ always stays HQ").
 */
export const SURFACE_HEADER = 'x-mangrove-surface';
export const HQ_SURFACE = 'hq';

export interface WorkspaceChange {
  from: string;
  to: string;
}

function announceWorkspaceChange(change: WorkspaceChange): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent<WorkspaceChange>(WORKSPACE_CHANGED_EVENT, { detail: change }),
  );
}

function urlOf(url: string | URL | Request): string {
  if (typeof url === 'string') return url;
  return url instanceof URL ? url.href : url.url;
}

/** #950: every call names its tab (see {@link TAB_CLIENT_ID}). */
function withTabId(init: RequestInit): RequestInit {
  const headers = new Headers(init.headers);
  headers.set(CLIENT_ID_HEADER, TAB_CLIENT_ID);
  return { ...init, headers };
}

/**
 * #803: which workspace this tab believes it is in, learned from the server
 * and sent back on every request. One per link chain (one per tab), never
 * shared.
 *
 *   - The first response of a page load tells the tab; before it, requests
 *     carry no belief and nothing is refused.
 *   - A response naming another workspace is a change, unless the request was
 *     this tab's own `tenant.activate` (its own switch). The tab is told, and
 *     KEEPS believing the workspace it shows until it reloads, so its writes
 *     stay refused in the meantime.
 *   - A response naming none (signed out, no workspace chosen, or an older API
 *     during a rollout) means "unknown": the belief is dropped, never read as a
 *     change, so a rollout can not set tabs reloading.
 *   - #823: a page that draws itself from its saved copy before any answer is
 *     SEEDED with the workspace that copy shows, so even its first call says
 *     which workspace it is for. A seed never replaces a learned belief.
 */
export function watchWorkspace(onChange: (change: WorkspaceChange) => void): {
  withBelief: (init: RequestInit | undefined) => RequestInit;
  learn: (url: string | URL | Request, res: Response) => void;
  seed: (tenantId: string | null | undefined) => void;
  believed: () => string | null;
} {
  let believed: string | null = null;
  return {
    seed(tenantId) {
      if (!believed && tenantId) believed = tenantId;
    },
    withBelief(init) {
      if (!believed) return init ?? {};
      const headers = new Headers(init?.headers);
      headers.set(WORKSPACE_HEADER, believed);
      return { ...init, headers };
    },
    learn(url, res) {
      const told = res.headers.get(WORKSPACE_HEADER);
      if (!told) {
        believed = null;
        return;
      }
      const previous = believed;
      // The batch path lists its calls comma-separated: /trpc/a,tenant.activate?batch=1
      const switchedHere = /\/trpc\/(?:[^?]*,)?tenant\.activate(?:[,?]|$)/.test(urlOf(url));
      if (previous && previous !== told && !switchedHere) {
        // Keep believing the workspace this page still SHOWS until it has
        // caught up (reloaded): a write sent meanwhile must still be refused.
        // Adopting the new one here let a save slip through on prod (30 Sep):
        // a focus refetch taught the tab the new workspace, and the save the
        // person sent before the reload landed there.
        onChange({ from: previous, to: told });
        return;
      }
      believed = told;
    },
    believed: () => believed,
  };
}

/** An app not bound to the session's workspace: sends no belief, learns nothing. */
const UNBOUND: ReturnType<typeof watchWorkspace> = {
  withBelief: (init) => init ?? {},
  learn: () => undefined,
  seed: () => undefined,
  believed: () => null,
};

/**
 * The API's database-free "are you up?" route, next to the tRPC endpoint: the
 * endpoint URL plus `/_alive`. Under `/trpc` on purpose — HQ reaches the API
 * through an nginx that forwards `/trpc` and nothing like `/health`, where a
 * probe would get the SPA's index.html back and read it as the API answering.
 */
export function aliveUrlFor(trpcUrl: string): string {
  return `${trpcUrl.replace(/\/+$/, '')}/_alive`;
}

/**
 * Whether the API itself answered. A 200 is not enough: only the route's own
 * body counts, so a proxy or SPA fallback can never pass for a server that is up.
 */
function probeAlive(f: typeof fetch, url: string): (signal: AbortSignal) => Promise<boolean> {
  return async (signal) => {
    const res = await f(url, { method: 'GET', credentials: 'omit', cache: 'no-store', signal });
    if (!res.ok) return false;
    const body = (await res.json().catch(() => null)) as { alive?: unknown } | null;
    return body?.alive === true;
  };
}

/**
 * #621: the context that sends a call on its own request. For a call that is
 * slow by design, because a model writes its answer (HQ's "Your brief" is
 * one): batched, it held up every call sent with it, so HQ's whole shell
 * waited 10-15 s for the brief and the slow-server notice said the server was
 * slow when it was not. Unbatched, the rest of the page answers at once, and
 * the call is left out of that notice, since its wait is not the server's.
 *
 *   trpc.ops['digest.me'].useQuery(undefined, { trpc: { context: SKIP_BATCH } })
 */
export const SKIP_BATCH = { skipBatch: true } as const;

/**
 * #1030: the context for a call sent as the page goes away (the usage beacon
 * when its tab is hidden or closed). It travels alone, with `keepalive`, so
 * the browser finishes it after the page is gone instead of cancelling it.
 * Small bodies only: a browser refuses a keepalive body over 64 KB.
 */
export const KEEPALIVE = { skipBatch: true, keepalive: true } as const;

/**
 * Build the standard link chain for `createTRPCClient` / `trpc.createClient`.
 * The runtime is router-agnostic; cast at the call site (or use the typed
 * helpers `createPosTrpcClient<TRouter>` / `createPosReactTrpcClient<TRouter>`).
 */
export function createPosTrpcLinks(opts: CreatePosTrpcLinksOptions): TRPCLink<AnyTRPCRouter>[] {
  const isDev = typeof process !== 'undefined' && process.env?.['NODE_ENV'] === 'development';

  const enabled =
    opts.loggerEnabled ?? ((op: { direction: 'up' | 'down' }) => isDev || op.direction === 'down');

  // tRPC's `headers` callback receives `{ opList }`; consumers don't need to
  // see that detail, so adapt the no-arg form they pass into the wider one.
  const headers = (() => {
    if (opts.headers === undefined) return undefined;
    if (typeof opts.headers !== 'function') return opts.headers;
    const fn = opts.headers;
    return () => fn();
  })();

  // #621: when a request is slow, ask the server whether it is up before the
  // notice names a cause. See slow-request.ts.
  setAwakeProbe(probeAlive(opts.fetch ?? fetch, aliveUrlFor(opts.url)));

  // #803: every call says which workspace this tab believes it is in, and
  // every answer says which one the session is in.
  const workspace =
    opts.workspaceBound === false
      ? UNBOUND
      : watchWorkspace(opts.onWorkspaceChanged ?? announceWorkspaceChange);
  // #823: the page's restored copy tells the link which workspace it shows.
  if (typeof window !== 'undefined') {
    window.addEventListener(WORKSPACE_SEED_EVENT, (e) => {
      workspace.seed((e as CustomEvent<{ tenantId?: string | null }>).detail?.tenantId);
    });
  }

  /** A call on a request of its own (`SKIP_BATCH`, `KEEPALIVE`). */
  const ownRequest = (keepalive: boolean) =>
    httpLink({
      url: opts.url,
      transformer: superjson,
      ...(headers !== undefined ? { headers } : {}),
      fetch(url, init) {
        const f = opts.fetch ?? fetch;
        return f(url, {
          ...withTabId(workspace.withBelief(init)),
          credentials: 'include',
          ...(keepalive ? { keepalive: true } : {}),
        }).then((res) => {
          workspace.learn(url, res);
          return res;
        });
      },
    });

  return [
    loggerLink({ enabled: enabled as never }),
    splitLink({
      condition: (op) => op.context['skipBatch'] === true,
      true: splitLink({
        condition: (op) => op.context['keepalive'] === true,
        true: ownRequest(true),
        false: ownRequest(false),
      }),
      false: httpBatchLink({
        url: opts.url,
        transformer: superjson,
        ...(headers !== undefined ? { headers } : {}),
        fetch(url, init) {
          const f = opts.fetch ?? fetch;
          // Every batched tRPC call in every app funnels through here, which
          // makes it the one honest place to notice that the server is taking
          // too long to answer (see slow-request.ts — Cloud Run cold starts run
          // ~29s). A SKIP_BATCH call is slow by design and stays out of it.
          const settle = markRequestStart();
          return f(url, { ...withTabId(workspace.withBelief(init)), credentials: 'include' }).then(
            (res) => {
              // A response of any status means the server is up (#598).
              settle({ responded: true });
              workspace.learn(url, res);
              return res;
            },
            (err: unknown) => {
              settle();
              throw err;
            },
          );
        },
      }),
    }),
  ] as TRPCLink<AnyTRPCRouter>[];
}
